import { useEffect, useMemo, useState, type ComponentType } from 'react';
import { Card } from 'react-bootstrap';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { trackerAiClient as api } from '@/app/TrackerAi/api/trackerAiClient';
import { useProject } from '../../context/ProjectContext';
import { buildDateRangeQueryOptions } from '../../utils/visibility-range';
import DataLoadError from '@/components/errors/DataLoadError';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import { CompetitorModal } from '../Modals';
import { getHelpHtml } from '../../utils/help/help-content';
import { normalizeHost } from '../../utils/favicon';
import { promptProviderComparisonColumnConfig } from '../../config/promptProviderComparisonColumnConfig';
import type {
  PromptBrandAggregation,
  PromptBrandAggregationBrand,
} from '../../types';

type AgGridWrapperProps = {
  table_name?: string;
  height?: string;
  columns: unknown[];
  data: unknown[];
  total?: number;
  settings?: Record<string, unknown>;
  otherOptions?: Record<string, unknown>;
};
const TypedAgGridWrapper = AgGridWrapper as ComponentType<AgGridWrapperProps>;

const TOP_BRANDS_LIMIT = 5;

function prefilledWebsiteUrl(domain: string | null | undefined): string {
  const host = domain ? normalizeHost(domain) : '';
  return host ? `https://${host}` : '';
}

function prefilledAliases(brand: PromptBrandAggregationBrand): string[] {
  const aliases = new Set<string>();
  const canonical = brand.canonicalName.trim().toLowerCase();

  for (const provider of brand.providers) {
    if (!provider.detected || !provider.surfaceText) continue;
    const surface = provider.surfaceText.trim();
    if (!surface || surface.toLowerCase() === canonical) continue;
    aliases.add(surface);
  }

  return [...aliases];
}

function brandRowKey(brand: PromptBrandAggregationBrand): string {
  if (brand.knownBrandId) return `known:${brand.knownBrandId}`;
  return `name:${brand.canonicalName.trim().toLowerCase()}`;
}

export function ProviderComparison({
  promptId,
  refreshKey = 0,
  onCreateCompetitor,
  onAddedCompetitor,
}: {
  promptId: string;
  refreshKey?: number;
  onCreateCompetitor?: (data: {
    brandName: string;
    websiteUrl: string;
    aliases: string[];
  }) => Promise<void>;
  onAddedCompetitor?: () => void;
}) {
  const [aggregation, setAggregation] = useState<PromptBrandAggregation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalBrand, setModalBrand] = useState<PromptBrandAggregationBrand | null>(null);
  const [saving, setSaving] = useState(false);
  const { visibilityRange, customFrom, customTo, modelFilterId, models } = useProject();

  const modelById = useMemo(
    () => new Map(models.map((model) => [model.id, model])),
    [models],
  );

  const rangeQueryOptions = useMemo(
    () =>
      buildDateRangeQueryOptions(visibilityRange, customFrom, customTo, {
        providerIds: modelFilterId ? [modelFilterId] : undefined,
      }),
    [visibilityRange, customFrom, customTo, modelFilterId],
  );

  const modalInitial = useMemo(
    () =>
      modalBrand
        ? {
            brandName: modalBrand.canonicalName,
            websiteUrl: prefilledWebsiteUrl(modalBrand.domain ?? modalBrand.detectedHost),
            aliases: prefilledAliases(modalBrand),
          }
        : undefined,
    [modalBrand],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    void api
      .getPromptBrandAggregation(promptId, rangeQueryOptions)
      .then((data) => {
        if (!cancelled) {
          setAggregation(data as PromptBrandAggregation);
          setShowAll(false);
          setError(false);
        }
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [promptId, refreshKey, rangeQueryOptions, retryNonce]);

  async function handleCreateCompetitor(data: {
    brandName: string;
    websiteUrl: string;
    aliases: string[];
  }) {
    if (!onCreateCompetitor) return;
    setSaving(true);
    try {
      await onCreateCompetitor(data);
      setModalOpen(false);
      setModalBrand(null);
      onAddedCompetitor?.();
    } finally {
      setSaving(false);
    }
  }

  function openCompetitorModal(brand: PromptBrandAggregationBrand) {
    setModalBrand(brand);
    setModalOpen(true);
  }

  const providerSlots =
    aggregation?.providers.filter((slot) => slot.status === 'executed') ??
    aggregation?.providers ??
    [];
  const allSlots = aggregation?.providers ?? [];
  const slots = providerSlots.length > 0 ? providerSlots : allSlots;
  const brands = aggregation?.brands ?? [];
  const uniqueBrands = brands.filter(
    (brand, index, list) =>
      list.findIndex((candidate) => brandRowKey(candidate) === brandRowKey(brand)) === index,
  );
  const visibleBrands = showAll ? uniqueBrands : uniqueBrands.slice(0, TOP_BRANDS_LIMIT);
  const hasHiddenBrands = uniqueBrands.length > TOP_BRANDS_LIMIT;

  const columns = useMemo(
    () =>
      promptProviderComparisonColumnConfig({
        slots,
        modelById,
        onAddCompetitor: onCreateCompetitor ? openCompetitorModal : undefined,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [slots, modelById, onCreateCompetitor],
  );

  if (loading && !error) {
    return <AgGridSkeleton height="280px" />;
  }

  return (
    <Card className="overflow-hidden ag-grid-in-card">
      <FalconCardHeader
        light
        titleTag="h6"
        className="py-2"
        title={
          <span className="d-inline-flex align-items-center gap-1">
            مقایسه برند در مدل‌ها
            <HelpTooltip html={getHelpHtml('brand_comparison_across_models')} />
          </span>
        }
      />

      {error && (
        <div className="px-3 py-4">
          <DataLoadError
            compact
            onRetry={() => setRetryNonce(n => n + 1)}
            isRetrying={loading}
          />
        </div>
      )}

      {!loading && !error && aggregation && aggregation.executedProviders === 0 && (
        <p className="py-5 text-center fs--1 text-500 mb-0">هنوز پاسخ پرووایدری نیست</p>
      )}

      {!loading && !error && aggregation && aggregation.executedProviders > 0 && uniqueBrands.length === 0 && (
        <p className="py-5 text-center fs--1 text-500 mb-0">هنوز برندی شناسایی نشده</p>
      )}

      {!loading && !error && uniqueBrands.length > 0 && slots.length > 0 && (
        <Card.Body className="pt-0 pb-2 px-3">
          {hasHiddenBrands && (
            <div className="d-flex justify-content-end mb-2">
              <button
                type="button"
                onClick={() => setShowAll(value => !value)}
                className="btn btn-link p-0 fs--1 fw-medium text-decoration-none"
              >
                {showAll ? 'نمایش برندهای برتر' : `نمایش همه برندها (${uniqueBrands.length})`}
              </button>
            </div>
          )}

          <TypedAgGridWrapper
            table_name="tracker_ai_prompt_provider_comparison"
            height="auto"
            columns={columns}
            data={visibleBrands}
            total={visibleBrands.length}
            settings={{ pagination: false, fullscreen: false, sideBar: false, export: { enabled: false } }}
            otherOptions={{
              rowHeight: 40,
              suppressRowHoverHighlight: false,
              autoSizeStrategy: { type: 'fitGridWidth' },
            }}
          />
        </Card.Body>
      )}

      <CompetitorModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setModalBrand(null);
        }}
        initial={modalInitial}
        onSubmit={handleCreateCompetitor}
        saving={saving}
      />
    </Card>
  );
}
